import { User } from './User';
import { Model } from '@/models/Model';
import modelService from '@/services/ModelService';
import { CompanyRole } from '@/@types/CompanyRole';
import { Business, Subscription } from '@/@types/billing';
import { PaymentMethodDetails } from '@/@types/billing/subscription/PaymentMethodDetails';
import { SubscriptionType } from '@/@types/enums/SubscriptionType';
import { PAID_STATUSES } from '@/constants/BillingConsts';

export interface RetentionPolicy {
  enabled: boolean;
  modifiedAt: Date;
  softDeleteDays: number; // e.g., 30 (Moves to Retention Page)
  hardDeleteDays: number;
}

export class Company implements Model {
  static parse(data: unknown): Company {
    return modelService.create(Company, data);
  }

  static getRolesList() {
    return {
      admin: 'Administrator',
      moderator: 'User Manager',
      user: 'Team Member',
    };
  }

  id: string | null = null;
  title = '';
  slug = '';
  roles: CompanyRole[] | null = null;
  user: string | null = null;
  logo: string | null = null;
  logoPreview: string | null = null;
  domain = '';
  createdAt = 0;
  isActivated: boolean | null = false;
  isAiEnabled: boolean | null = false;
  isBilling = false;
  // Entitlement keys the company has been granted (e.g. 'howto' for Guides, 'kb' for
  // Snaplore). Granting happens at signup or via Settings; it is NOT proof of payment —
  // always pair with `isPaid` (D1: signup grants, payment gates, SNAP-2231/2241).
  enabledProducts: string[] = [];
  // Backend verdict on product access, computed server-side: `howto` is true only when the
  // company pays for Guides itself or was granted Guides without payment (a KB subscription
  // never unlocks it). Absent on older backends: callers fall back to enabledProducts + isPaid.
  // `kb` is Snaplore itself: false when the company pays only for Guides (SNAP-2241).
  productAccess: { howto?: boolean; kb?: boolean } | null = null;
  // Whether the signed-in user owns this company, on the `/me` `companies[]` entries only.
  isOwner: boolean | null = null;
  paddle: {
    status: SubscriptionType | null;
    subscriptionId: Subscription['id'] | null;
    businessId: Business['id'] | null;
    payment?: PaymentMethodDetails | null;
    priceId?: string | null;
    planName?: string | null;
    priceUnitAmount?: string | null;
    // The Paddle price's `customData.app` tag for the company's current subscription:
    // 'guides' for a Guides subscription, undefined for Snaplore/legacy (M3 vocabulary).
    app?: 'guides' | null;
  } = null;
  retention: RetentionPolicy;

  /**
   * `/me` `companies[]` entries come from a projection, so the subscription arrives as dotted
   * top-level keys (`'paddle.status'`, `'paddle.app'`) that the field-by-field model load drops.
   * Fold them into the nested `paddle` object so every consumer reads one shape.
   */
  afterLoad(data?: any): void {
    // The model loader copies only truthy fields, and `false` is half of this answer.
    if (typeof data?.isOwner === 'boolean') this.isOwner = data.isOwner;
    if (this.paddle || !data || typeof data !== 'object') return;
    const dottedKeys = Object.keys(data).filter((key) => key.startsWith('paddle.'));
    if (!dottedKeys.length) return;
    const paddle: Record<string, unknown> = {};
    dottedKeys.forEach((key) => {
      paddle[key.slice('paddle.'.length)] = data[key];
    });
    this.paddle = { status: null, subscriptionId: null, businessId: null, ...paddle };
  }

  canEdit(user: User) {
    return this.user === user.id || user.role === 'owner' || user.role === 'admin' || user.role === 'moderator';
  }

  get isPaid() {
    return !this.isBilling ? true : !this.paddle.status ? false : PAID_STATUSES.includes(this.paddle.status);
  }

  getDomainAddress() {
    const defaultDomain: string = process.env.VUE_APP_URL;
    return this.domain ? 'https://' + this.domain : defaultDomain;
  }

  setUserStatus(userID: string, status: string) {
    const roles: CompanyRole[] = this.roles;

    const items = roles.filter((item: CompanyRole) => item.user === userID);
    items.forEach((e: any) => (e.status = status));
  }

  getUserStatus(userID: string) {
    const roles: CompanyRole[] = this.roles;
    let status = null;

    const item = roles.filter((role: CompanyRole) => role.user === userID)[0];

    if (item) {
      status = item.status;
    }

    return status;
  }
}
